<script setup lang="ts">
import { ref, onMounted, computed } from 'vue';
import { useAuth } from 'vue-auth3';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useCookies } from 'vue3-cookies';

import efPromiseButton from '../../components/efPromiseButton.vue';

const { t } = useI18n();
const auth = useAuth();
const router = useRouter();
const { cookies } = useCookies();

const loading = ref<boolean>(true);
const error = ref<string | null>(null);
const unauthorized = ref<boolean | null>(null);

const data = ref({
	email: null,
	password: null,
	remember: null,
});

const isValid = computed(() => {
	return data.value.email && data.value.password;
});

const selfRegistrationEnabled = computed<boolean>(() => {
	return globalThis.env?.app?.self_registration === true;
});

type OAuthService = {
	id?: number;
	key: string;
	icon: string;
	redirect?: string;
};

const oauth = ref<Record<string, OAuthService>>({
	/*google: { id: undefined, key: 'google', icon: 'google.svg' },
  apple: { id: undefined, key: 'apple', icon: 'apple.svg' },
  keycloak: { id: undefined, key: 'keycloak', icon: 'keycloak.svg' },*/
});

onMounted(async () => {
	// Setup the OAuth2 services
	if (globalThis.env?.sso?.microsoft_redirect_url != '') {
		oauth.value['microsoft'] = {
			id: 1,
			key: 'microsoft',
			icon: 'microsoft.svg',
			redirect: globalThis.env?.sso?.microsoft_redirect_url,
		};
	}

	if (globalThis.env?.sso?.authentik_redirect_url != '') {
		oauth.value['authentik'] = {
			id: 1,
			key: 'authentik',
			icon: 'authentik.svg',
			redirect: globalThis.env?.sso?.authentik_redirect_url,
		};
	}

	// Check if redirected from OAuth2 provider
	if (router.currentRoute.value.query?.token) {
		auth.token(null, router.currentRoute.value.query.token as string);
		auth.fetch().then(() => {
			// if successful, redirect to home and store the login methode
			cookies.set('loginMethod', 'microsoft');
			router.push({ name: 'Home' });
		});
	}

	// Check if redirected from OAuth2 provider
	if (router.currentRoute.value.query?.code) {
		auth.token(null, router.currentRoute.value.query.token as string);
		auth.fetch().then(() => {
			// if successful, redirect to home and store the login methode
			cookies.set('loginMethod', 'authentik');
			router.push({ name: 'Home' });
		});
	}

	// Check for autologin
	else if (cookies.get('loginMethod')) {
		const methode = cookies.get('loginMethod');
		oauth_login(oauth.value[methode]);
	}

	// Disable loading
	else {
		loading.value = false;
	}
});

// Define the login method
async function login() {
	// Reset the messages
	error.value = null;
	unauthorized.value = null;

	// Prepare login parameter
	const redirectPath = router.currentRoute.value.redirectedFrom?.fullPath;

	// Try to authenticate
	auth.login({
		data: {
			email: data.value.email,
			password: data.value.password,
			remember: data.value.remember,
		},
		redirect: redirectPath ? { path: redirectPath } : { name: 'Home' },
	}).catch((err) => {
		if (err.response?.status === 401) {
			unauthorized.value = true;
		} else {
			console.warn(err);
			error.value = 'Service not available. Please try again later.';
		}
	});
}

// Define the login method
function oauth_login(service: OAuthService) {
	return new Promise<void>(() => {
		globalThis.location.href = service.redirect || '/';
		// Wait forever
	});
}
</script>

<template>
	<div class="row justify-content-center" style="max-width: 512px">
		<div v-if="loading" class="d-flex flex-column align-items-center justify-content-center">
			<div class="spinner-border text-primary">
				<span class="visually-hidden">{{ t('pages.auth.login.loading') }}</span>
			</div>
		</div>

		<div v-else class="d-flex flex-column align-items-center justify-content-center">
			<div class="pt-4 pb-2">
				<h5 class="card-title text-center pb-0 fs-4">{{ t('pages.auth.login.title') }}</h5>
				<p class="text-center small">{{ t('pages.auth.login.text') }}</p>
			</div>

			<div v-if="unauthorized" class="col-12">
				<div class="alert alert-danger" role="alert">{{ t('pages.auth.login.error.invalidLogin') }}</div>
			</div>

			<div v-if="error" class="col-12">
				<div class="alert alert-danger" role="alert">{{ error }}</div>
			</div>

			<form @submit.prevent="" class="row g-3 w-100">
				<div class="col-12">
					<label for="username" class="form-label">{{ t('pages.auth.login.email') }}</label>
					<div class="input-group has-validation">
						<span class="input-group-text" id="inputGroupPrepend">@</span>
						<input
							v-model="data.email"
							type="text"
							class="form-control"
							id="username"
							autocomplete="username"
						/>
						<div class="invalid-feedback">Please enter your username.</div>
					</div>
				</div>

				<div class="col-12">
					<label for="password" class="form-label">{{ t('pages.auth.login.password') }}</label>
					<div class="input-group has-validation">
						<input
							v-model="data.password"
							type="password"
							class="form-control"
							id="password"
							autocomplete="current-password"
						/>
						<div class="invalid-feedback">Please enter your password!</div>
					</div>
					<p class="small mb-0 float-end">
						<router-link to="forgot-password">{{ t('pages.auth.login.passwordReset.link') }}</router-link>
					</p>
				</div>

				<div class="col-12 mt-0">
					<div class="form-check">
						<input
							v-model="data.remember"
							class="form-check-input"
							type="checkbox"
							name="remember"
							value="true"
							id="rememberMe"
						/>
						<label class="form-check-label" for="rememberMe">{{ t('pages.auth.login.rememberMe') }}</label>
					</div>
				</div>

				<div class="col-12">
					<efPromiseButton :callback="login" class="w-100" btnClass="btn-primary w-100" :disabled="!isValid">
						{{ t('pages.auth.login.signIn') }}
					</efPromiseButton>
				</div>

				<div v-if="selfRegistrationEnabled" class="col-12">
					<p class="small mb-0">
						<span>{{ t('pages.auth.login.register.text') }}&nbsp;</span>
						<a href="/register">{{ t('pages.auth.login.register.link') }}</a>
					</p>
				</div>

				<div class="divider-wrapper">
					<span class="divider">{{ t('pages.auth.login.social.divider') }}</span>
				</div>

				<template v-for="(service, ind) in oauth" :key="ind">
					<div v-if="service.id != null" class="col-12">
						<efPromiseButton
							:callback="() => oauth_login(service)"
							class="w-100"
							btnClass="btn-outline-secondary btn-social w-100"
						>
							<img
								class="social-logo me-2"
								:src="`/img/logos-sso/${service.icon}`"
								:alt="`logo-ssp-${service.key}`"
							/>
							<span>{{ t(`pages.auth.login.social.${service.key}`) }}</span>
						</efPromiseButton>
					</div>
				</template>
			</form>
		</div>
	</div>
</template>

<style lang="scss">
.divider-wrapper:last-child {
	display: none;
}

.btn-social {
	--bs-btn-color: #6c757d;
	--bs-btn-border-color: #6c757d;
	--bs-btn-hover-color: #fff;
	--bs-btn-hover-bg: #6c757d;
	--bs-btn-hover-border-color: #6c757d;
	--bs-btn-focus-shadow-rgb: 108, 117, 125;
	--bs-btn-active-color: #fff;
	--bs-btn-active-bg: #6c757d;
	--bs-btn-active-border-color: #6c757d;
	--bs-btn-active-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
	--bs-btn-disabled-color: #6c757d;
	--bs-btn-disabled-bg: transparent;
	--bs-btn-disabled-border-color: #6c757d;
	--bs-gradient: none;
}

.social-logo {
	height: 20px;
	display: inline-block;
	vertical-align: text-bottom;
}
</style>
